import type { ReactNode } from "react";
import { Badge } from "./Badge";
import { BellOffIcon, PinIcon } from "./icons";
import { UiLink } from "./link";
import { type DeliveryStatus, Ticks } from "./Ticks";
import { cn, focusRing } from "./utils";

type RowShellProps = {
  href?: string;
  onClick?: () => void;
  selected?: boolean;
  className: string;
  children: ReactNode;
};

function RowShell({ href, onClick, selected, className, children }: RowShellProps) {
  const classes = cn("text-left text-ink no-underline", focusRing, className);
  if (href !== undefined) {
    return (
      <UiLink href={href} onClick={onClick} aria-current={selected ? "true" : undefined} className={classes}>
        {children}
      </UiLink>
    );
  }
  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        aria-pressed={selected}
        className={cn(classes, "w-full cursor-pointer")}
      >
        {children}
      </button>
    );
  }
  return <div className={classes}>{children}</div>;
}

export type ListRowProps = {
  leading?: ReactNode;
  title: ReactNode;
  subtitle?: ReactNode;
  trailing?: ReactNode;
  href?: string;
  onClick?: () => void;
  selected?: boolean;
  titleClassName?: string;
  className?: string;
};

// A generic row: leading avatar or icon, a title with a subtitle, and something at the end.
export function ListRow({
  leading,
  title,
  subtitle,
  trailing,
  href,
  onClick,
  selected,
  titleClassName,
  className,
}: ListRowProps) {
  const body = (
    <>
      {leading}
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className={cn("truncate text-base font-bold", titleClassName)}>{title}</span>
        {subtitle && (
          <span className="flex min-w-0 items-center gap-[5px] text-[13.5px] text-ink-secondary">
            {subtitle}
          </span>
        )}
      </span>
    </>
  );
  // A trailing action is its own control, so it sits beside the row link rather than inside it.
  if (trailing && (href !== undefined || onClick)) {
    return (
      <div className={cn("flex items-center gap-3.5 px-5 py-2.5", className)}>
        <RowShell
          href={href}
          onClick={onClick}
          selected={selected}
          className="flex min-w-0 flex-1 items-center gap-3.5 rounded-tile"
        >
          {body}
        </RowShell>
        {trailing}
      </div>
    );
  }
  return (
    <RowShell
      href={href}
      onClick={onClick}
      selected={selected}
      className={cn("flex items-center gap-3.5 px-5 py-2.5", className)}
    >
      {body}
      {trailing}
    </RowShell>
  );
}

export type ChatRowProps = {
  title: string;
  avatar: ReactNode;
  time: string;
  preview?: ReactNode;
  /** Name shown in bold before the preview, for group chats. */
  sender?: string;
  previewIcon?: ReactNode;
  typing?: boolean;
  /** What you started writing here and have not sent, shown as "Draft: ..." in place of the preview. */
  draft?: string;
  missed?: boolean;
  unread?: number;
  /** Marked unread with no new messages: a dot, until the chat is opened. */
  markedUnread?: boolean;
  mention?: boolean;
  muted?: boolean;
  /** When a mute ends, such as "14:30", shown beside the muted bell. Empty for one with no end. */
  mutedUntil?: string;
  pinned?: boolean;
  /** Delivery of your own last message. */
  status?: DeliveryStatus;
  tag?: ReactNode;
  variant?: "default" | "card";
  density?: "comfortable" | "compact";
  href?: string;
  onClick?: () => void;
  selected?: boolean;
};

export function ChatRow({
  title,
  avatar,
  time,
  preview,
  sender,
  previewIcon,
  typing = false,
  draft,
  missed = false,
  unread = 0,
  markedUnread = false,
  mention = false,
  muted = false,
  mutedUntil,
  pinned = false,
  status,
  tag,
  variant = "default",
  density = "comfortable",
  href,
  onClick,
  selected = false,
}: ChatRowProps) {
  const compact = density === "compact";
  const hasUnread = unread > 0;
  const dot = !hasUnread && markedUnread;
  const previewText = typing ? (
    <span className="truncate italic text-blue">typing…</span>
  ) : draft ? (
    <span className="truncate">
      <span className="font-semibold text-blue">Draft: </span>
      {draft}
    </span>
  ) : (
    <span className={cn("flex min-w-0 items-center gap-[5px]", missed && "font-semibold text-scarlet")}>
      {status && !missed && <Ticks status={status} size={17} />}
      {previewIcon}
      <span className="truncate">
        {sender && <b className="font-semibold text-ink">{sender}: </b>}
        {preview}
      </span>
    </span>
  );

  return (
    <RowShell
      href={href}
      onClick={onClick}
      selected={selected}
      className={cn(
        "flex items-center",
        compact ? "gap-3 rounded-tile p-2.5" : "gap-3.5 px-5 py-2.5",
        variant === "card" && "mx-3 mb-1.5 rounded-card border border-line bg-surface px-2.5",
        selected ? "bg-surface-header" : variant === "default" && "hover:bg-surface-header/60",
      )}
    >
      {avatar}
      <span className={cn("flex min-w-0 flex-1 flex-col", compact ? "gap-0.5" : "gap-[3px]")}>
        <span className="flex items-baseline gap-2">
          <span
            className={cn("flex min-w-0 flex-1 items-center gap-2", compact ? "text-[15px]" : "text-base")}
          >
            <span className="truncate font-bold">{title}</span>
            {tag}
          </span>
          <span
            className={cn(
              "flex-none text-xs",
              (hasUnread || dot) && !muted
                ? "font-bold text-blue"
                : compact
                  ? "text-ink-secondary"
                  : "text-ink-meta",
            )}
          >
            {time}
          </span>
        </span>
        <span
          className={cn(
            "flex min-w-0 items-center gap-1.5 text-ink-secondary",
            compact ? "text-[13.5px]" : "text-sm",
          )}
        >
          <span className="flex min-w-0 flex-1">{previewText}</span>
          {pinned && <PinIcon size={15} className="text-ink-muted" label="Pinned" />}
          {muted && (
            <span className="flex flex-none items-center gap-0.5 text-ink-muted">
              <BellOffIcon size={16} label={mutedUntil ? `Muted until ${mutedUntil}` : "Muted"} />
              {mutedUntil && (
                <span aria-hidden="true" className="text-[11.5px] tabular-nums">
                  {mutedUntil}
                </span>
              )}
            </span>
          )}
          {mention && <Badge variant="mention" size={compact ? "sm" : "md"} />}
          {hasUnread && (
            <Badge variant={muted ? "muted" : "unread"} count={unread} size={compact ? "sm" : "md"} />
          )}
          {dot && (
            <span
              role="img"
              aria-label="Marked as unread"
              className={cn(
                "flex-none rounded-full",
                compact ? "mx-1 h-2.5 w-2.5" : "mx-1.5 h-3 w-3",
                muted ? "bg-ink-muted" : "bg-gold",
              )}
            />
          )}
        </span>
      </span>
    </RowShell>
  );
}
